import React from 'react'
import { CloseCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import {
  selectExtensionAPI,
  loadEmailContent
} from '@SonarCX/slices/extensionAPI'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useStyles from './styles'

export type EmailProps = {
  email: Media
  setValues: React.Dispatch<React.SetStateAction<LocalTask>>
  triggerSave: () => void
  inModal?: boolean
}

const Email: React.FC<EmailProps> = ({
  email,
  setValues,
  triggerSave,
  inModal
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { loadedEmails } = useAppSelector(selectExtensionAPI)

  const { setTitle, setSubtext, setReason, setV2, setAction } = useAreYouSure()

  const loadedEmail = React.useMemo(
    () => loadedEmails[email.id],
    [loadedEmails, email.id]
  )

  const classes = useStyles()

  // Read email content from media file
  React.useEffect(() => {
    dispatch(loadEmailContent(email))
  }, [dispatch, email])

  const gotoEmail = React.useCallback(() => {
    document.querySelector(`#email-${email.id}`)?.scrollIntoView({
      behavior: 'smooth'
    })
  }, [email])

  const onDelete = React.useCallback(() => {
    setTitle('Remove email?')
    setSubtext('')
    setReason('Are you sure you want to remove this email?')
    setV2(true)
    setAction(() => {
      setValues(vals => {
        const newMedia = (vals.media ?? []).filter(m => m.id !== email.id)
        return { ...vals, media: newMedia }
      })
      triggerSave()
    })
  }, [
    setTitle,
    setSubtext,
    setReason,
    setV2,
    setAction,
    email,
    setValues,
    triggerSave
  ])

  if (!loadedEmail) {
    return <div className={classes.root}>Loading...</div>
  }

  return (
    <div className={classes.root}>
      <div onClick={gotoEmail} className={classes.email}>
        <div className={classes.subject}>{loadedEmail.subject}</div>

        <div className={classes.info}>
          <div className={classes.from}>{loadedEmail.sender}</div>

          <div className={classes.date}>{loadedEmail.receivedTime}</div>
        </div>
      </div>

      <div className={classes.delete}>
        <CloseCircle
          color={theme.palette.primary.main}
          size={theme.size.base}
          onClick={onDelete}
          className={classes.deleteButton}
        />
      </div>
    </div>
  )
}

export default Email
